import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/auth";

/* ============================================================
   /story — Cinematic video-first landing page.

   Purpose: TikTok/Reels/YouTube traffic lands here. Auto-plays the
   "Out of the Ashes" narration over Paul's hero photo, previews
   5 hand-picked tracks, drives to signup.

   Companion page to /about (which is text-first founder narrative).
   ============================================================ */

// Hand-picked "best foot forward" tracks — one per major emotional lane
const FEATURED_TITLES = [
  "Primitive Theory",
  "The Way of the Bovine",
  "Keep the Monsters Away",
  "Dance Through the Fire",
  "Rhythm of Life",
];

export default function Story() {
  const { user } = useAuth();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    axios.get(`${API}/charts?limit=100`)
      .then(r => {
        const all = Array.isArray(r.data) ? r.data : (r.data.tracks || []);
        const picked = FEATURED_TITLES
          .map(t => all.find(x => (x.title || "").toLowerCase() === t.toLowerCase()))
          .filter(Boolean);
        setFeatured(picked);
      })
      .catch(() => {});
  }, []);

  const toggleNarration = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  return (
    <div data-testid="story-page" className="relative min-h-screen bg-black text-white">
      {/* ============ HERO ============ */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('/story/paul_hero.png')",
            filter: "contrast(1.05) saturate(0.95)",
          }}
        />
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black" />
        {/* Subtle grain */}
        <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/></svg>\")" }}
        />

        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 pb-24 pt-20">
          <div className="font-mono text-xs tracking-[0.5em] text-yt-yellow mb-4" data-testid="story-eyebrow">
            OUT OF THE ASHES
          </div>
          <h1 className="font-display text-6xl md:text-8xl lg:text-9xl tracking-tight leading-[0.85] mb-8">
            STEAD<br/>
            <span className="text-yt-red">FAST.</span>
          </h1>
          <p className="font-body text-xl md:text-2xl text-white/85 max-w-2xl leading-tight mb-10">
            Alberta farmer. Brain injury survivor.
            <br className="hidden md:block" />
            Wrote 300 songs. Built an AI platform from scratch.
          </p>

          {/* Narration player */}
          <div data-testid="story-narration-block" className="bg-black/70 backdrop-blur-sm border-2 border-yt-yellow p-6 max-w-2xl">
            <div className="font-mono text-[10px] tracking-[0.4em] text-yt-yellow mb-3">▶ LISTEN — 60 SECONDS</div>
            <div className="flex items-center gap-4">
              <button
                data-testid="story-narration-toggle"
                onClick={toggleNarration}
                className="w-16 h-16 flex-shrink-0 bg-yt-yellow text-black rounded-full font-display text-3xl shadow-brutal hover:bg-white transition-colors"
                aria-label={playing ? "Pause narration" : "Play narration"}
              >
                {playing ? "▐▐" : "▶"}
              </button>
              <div className="min-w-0">
                <div className="font-display text-2xl md:text-3xl tracking-tight leading-none">The Story, In My Own Voice</div>
                <div className="font-mono text-xs text-yt-muted mt-2">Narrated by Paul DeGagné · Alberta · one minute</div>
              </div>
            </div>
            <audio
              ref={audioRef}
              onEnded={() => setPlaying(false)}
              onPause={() => setPlaying(false)}
              onPlay={() => setPlaying(true)}
              preload="metadata"
              src="/media/out_of_the_ashes_v2_onyx.mp3"
              data-testid="story-narration-audio"
            />
          </div>
        </div>
      </section>

      {/* ============ THE TURN ============ */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-24">
        <div className="font-mono text-xs tracking-[0.5em] text-yt-yellow mb-6">THE TURN</div>
        <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[0.95] mb-8">
          They said the farm was done.<br/>
          <span className="text-white/60">That I was done.</span><br/>
          <span className="text-yt-red">One word said otherwise.</span>
        </h2>
        <p className="font-body text-lg md:text-xl text-white/85 leading-relaxed max-w-3xl">
          A brain injury took my memory. The left side of my mind went quiet.
          Then I lost my dad — the man who taught me the land.
          A cyber scam wiped out the last dollar I had.
        </p>
        <p className="font-body text-lg md:text-xl text-white/85 leading-relaxed max-w-3xl mt-6">
          So I got back up. <strong className="text-yt-yellow">Weld by weld. Word by word.</strong>{" "}
          I wrote three hundred songs. I built an AI platform from scratch, at my kitchen table,
          on this farm. I called it <strong className="text-white">Young Tongues.</strong>
        </p>
      </section>

      {/* ============ THE PROOF (tracks) ============ */}
      <section className="border-y-4 border-yt-yellow bg-yt-surface">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20">
          <div className="font-mono text-xs tracking-[0.5em] text-yt-red mb-4">● THE PROOF</div>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[0.9] mb-4">
            THE WORK<br/><span className="text-yt-yellow">SPEAKS FOR ITSELF.</span>
          </h2>
          <p className="font-mono text-sm text-yt-muted mb-10">
            Five tracks from the library. All AI-produced. All lyrics written by Paul.
          </p>

          <div className="space-y-3">
            {featured.length === 0 && (
              <div className="font-mono text-sm text-yt-muted p-6 border-2 border-dashed border-yt-border">
                Loading tracks…
              </div>
            )}
            {featured.map(t => (
              <FeaturedTrack key={t.id} track={t} />
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link to="/charts" data-testid="story-see-full-library" className="inline-block bg-yt-yellow text-black font-display text-xl tracking-wider px-8 py-4 shadow-brutal hover:bg-white transition-colors">
              SEE THE FULL LIBRARY ▶
            </Link>
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-24 text-center">
        <div className="font-mono text-xs tracking-[0.5em] text-yt-yellow mb-6">YOUR TURN</div>
        <h2 className="font-display text-4xl md:text-7xl tracking-tight leading-[0.9] mb-8">
          If a farmer with a<br/>
          <span className="text-yt-red">brain injury</span> can do this,<br/>
          <span className="text-white">what's your excuse?</span>
        </h2>
        <p className="font-body text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-10">
          Young Tongues is a live AI rap battle platform + AI music charts.
          Write bars. Drop songs. Compete for the crown. Free to start.
        </p>
        {!user && (
          <Link to="/signup?ref=story" data-testid="story-cta-signup" className="inline-block bg-yt-red text-white font-display text-2xl tracking-wider px-10 py-5 shadow-brutal hover:bg-white hover:text-black transition-colors">
            SIGN UP FREE ▶
          </Link>
        )}
        {user && (
          <Link to="/battles" data-testid="story-cta-battle" className="inline-block bg-yt-red text-white font-display text-2xl tracking-wider px-10 py-5 shadow-brutal hover:bg-white hover:text-black transition-colors">
            STEP INTO THE ARENA ▶
          </Link>
        )}
        <div className="mt-6 font-mono text-xs text-yt-muted">
          Or read the full founder story on <Link to="/about" className="text-yt-yellow hover:underline">/about</Link>.
        </div>
      </section>

      {/* Signature closer */}
      <section className="border-t border-yt-border py-12 text-center">
        <div className="font-display text-3xl tracking-tight text-white/40">YOUNG TONGUES</div>
        <div className="font-mono text-xs text-yt-yellow mt-2 tracking-[0.4em]">STEADFAST.</div>
      </section>
    </div>
  );
}

function FeaturedTrack({ track }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };
  return (
    <div data-testid={`story-track-${track.id}`} className="flex items-center gap-4 bg-black border-2 border-yt-border hover:border-yt-yellow transition-colors p-4">
      <button
        onClick={toggle}
        data-testid={`story-track-play-${track.id}`}
        className="w-14 h-14 flex-shrink-0 bg-yt-yellow text-black font-display text-xl shadow-brutal hover:bg-white transition-colors"
        aria-label={playing ? `Pause ${track.title}` : `Play ${track.title}`}
      >
        {playing ? "▐▐" : "▶"}
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-display text-xl md:text-2xl tracking-tight truncate">{track.title}</div>
        <div className="font-mono text-[10px] text-yt-muted tracking-widest">
          {track.publish_as || "RodeoRhymes"} · {(track.style || "").replace(/_/g, " ").toUpperCase()}
        </div>
      </div>
      <Link
        to={`/tracks/${track.id}`}
        className="hidden sm:inline-block font-mono text-xs text-yt-yellow tracking-widest border border-yt-yellow px-3 py-2 hover:bg-yt-yellow hover:text-black transition-colors"
      >
        FULL PAGE ▶
      </Link>
      <audio
        ref={audioRef}
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        preload="none"
        src={track.audio_url || track.preview_url}
      />
    </div>
  );
}
